import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, Modal} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useCallback, useMemo, useRef, useState} from 'react';

import {makeAnalyticsEventId, trackEventWithModule} from 'analytics/Analytics';
import {UPDATE_FLOWCHART_MODAL_TYPE} from 'constants/analytics-products/WorkflowAnalyticsConstants';
import {
  ActionMethod,
  ActionOrigin,
  ANALYTICS_MODULE,
  CloseType,
  COMMON_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {isNodeTesting} from 'lib/Browser';
import notifications from 'notifications';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import ImprovedProcessUpdateFlowchartModal from 'processes/modals/ImprovedProcessUpdateFlowchartModal';
import ExecutionFlowchartPane from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/ExecutionFlowchartPane';
import {useExecutionFlowchartDraftStateForProcess} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/execution/useExecutionFlowchartDraftState';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import {PROCESS_FLOWCHART_CONFIG_EDITOR_QUERY} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useProcessFlowchartConfigQuery';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import MappingInProgressIndicator from './MappingInProgressIndicator';
import ReadOnlyFlowchart from './ReadOnlyFlowchart';
import type {ViewerStaticTemplateFlowchart} from './ReadOnlyFlowchartTypes';

const HEADER_Z = 5;

const useTaskGroupFlowchartExecutionViewerStyles = createStyles((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    flexShrink: 0,
    width: '100%',
    zIndex: HEADER_Z,
    backgroundColor: theme.white,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 48,
  },
  heading: {
    marginLeft: theme.spacing.md,
    color: theme.colors.gray[7],
  },
  toolbar: {
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    columnGap: theme.spacing.xs,
    marginRight: theme.spacing.md,
  },
  editButtonActive: {
    '&:hover': {
      color: theme.colors.blue[4],
    },
  },
  pane: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
  },
  mappingInProgressIndicator: {
    position: 'absolute',
    top: theme.spacing.xs,
    marginLeft: theme.spacing.md,
    zIndex: HEADER_Z,
  },
}));

export interface TaskGroupFlowchartExecutionViewerProps {
  flowchartProcessId: string;
  flowchartTitle: string;
  rootProcessTaskSchemaId: string;
  sourceType: string;
  editDisabledMessage: string | null;
  canEditFlowchart: boolean;
  onFlowchartModified: () => void;
  isFlowchartMappingInProgress?: boolean;
  actionOrigin: ActionOrigin;
  onSetProcess?: (processId: string) => void;
  activeProcessId?: string;
  lineageRootTaskId?: string;
  displayOnlyFlowchart?: ViewerStaticTemplateFlowchart;
  displayOnlyProcessIdsByNodeConfigId?: Record<string, string>;
}

/**
 * Execution flowchart for a task-group root process: header, optional "update flowchart"
 * modal, and {@link ExecutionFlowchartPane}. Call `onFlowchartModified` after saves so lineage
 * data refetches (see `ProcessTaskGroupLineageViewer`).
 */
export default function TaskGroupFlowchartExecutionViewer(
  props: TaskGroupFlowchartExecutionViewerProps
): JSX.Element {
  const [pcmEditorOpened, {open: openPcmEditor, close: closePcmEditor}] = useDisclosure(false);

  const {classes, cx} = useTaskGroupFlowchartExecutionViewerStyles();
  const {draftState, setDraftState, clearDraft, readinessSnapshot, processIdsByNodeConfigId} =
    useExecutionFlowchartDraftStateForProcess({
      processId: props.flowchartProcessId,
      editorMode: pcmEditorOpened ? FlowchartEditorMode.PCM_EDITOR : FlowchartEditorMode.EXECUTION_VIEWER,
      lineageRootTaskId: props.lineageRootTaskId,
      skip: props.displayOnlyFlowchart != null && !pcmEditorOpened,
    });

  const {onSetProcess, activeProcessId} = props;
  const effectiveProcessIdsByNodeConfigId =
    props.displayOnlyProcessIdsByNodeConfigId ?? processIdsByNodeConfigId;
  const effectiveProcessIdsRef = useRef(effectiveProcessIdsByNodeConfigId);
  effectiveProcessIdsRef.current = effectiveProcessIdsByNodeConfigId;
  const onSetProcessRef = useRef(onSetProcess);
  onSetProcessRef.current = onSetProcess;
  const onNodeNavigateClick = useMemo(() => {
    if (onSetProcess == null) {
      return undefined;
    }
    return (nodeConfigId: string) => {
      const processId = effectiveProcessIdsRef.current?.[nodeConfigId];
      if (processId != null) {
        onSetProcessRef.current?.(processId);
      }
    };
  }, [onSetProcess]);

  const selectedNodeConfigId = useMemo(() => {
    if (activeProcessId == null || effectiveProcessIdsByNodeConfigId == null) {
      return undefined;
    }
    return Object.entries(effectiveProcessIdsByNodeConfigId).find(
      ([, processId]) => processId === activeProcessId
    )?.[0];
  }, [activeProcessId, effectiveProcessIdsByNodeConfigId]);

  const [pcmEditorEventGroupId, setPcmEditorEventGroupId] = useState('');
  // Snapshot of the draft taken when the PCM editor opens. The editor mutates the shared draft
  // as the user edits, so on Cancel/Exit we restore this snapshot to keep the inline execution
  // viewer from picking up unsaved changes (mirrors the ACM `lastSavedDraftState` pattern in
  // `CreateProcessModal.useCreateProcessFlowchartPaneState`).
  const [preEditDraftSnapshot, setPreEditDraftSnapshot] = useState<FlowchartConfigWithMetadata | null>(null);

  const revertDraftAndClose = useCallback((): void => {
    setDraftState(preEditDraftSnapshot);
    closePcmEditor();
  }, [closePcmEditor, preEditDraftSnapshot, setDraftState]);

  const handlePcmEditorExit = useCallback(() => {
    if (pcmEditorEventGroupId !== '') {
      trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
        event_group_id: pcmEditorEventGroupId,
        action_origin: props.actionOrigin,
        action_method: ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION,
        close_type: CloseType.EXIT,
        modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
      });
    }
    revertDraftAndClose();
  }, [pcmEditorEventGroupId, props.actionOrigin, revertDraftAndClose]);

  const handlePcmEditorSubmit = useCallback(async (): Promise<void> => {
    closePcmEditor();
    // Refetch the flowchart config and then clear the draft so it rebuilds the draft from the new
    // data
    try {
      await getApolloClient().query({
        query: PROCESS_FLOWCHART_CONFIG_EDITOR_QUERY,
        variables: {flowchartConfigId: readinessSnapshot.data!.flowchartConfigId},
        fetchPolicy: 'network-only',
      });
    } catch (err) {
      notifications.error(
        'Flowchart saved but failed to refresh. Please reload the page to see the latest flowchart.'
      );
      throw err;
    } finally {
      clearDraft();
    }
  }, [clearDraft, closePcmEditor, readinessSnapshot.data]);

  return (
    <Box
      className={classes.root}
      {...getTestProps({component: 'TaskGroupFlowchartExecutionViewer', element: 'root'})}
    >
      <Box className={classes.header}>
        <Box className={classes.heading}>
          <BenchlingIcon icon="workflow" /> &nbsp;
          {props.flowchartTitle}
        </Box>
        {props.canEditFlowchart && (
          <Box className={classes.toolbar}>
            <TooltipWrapper tooltipTitle={props.editDisabledMessage}>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_WHITE}
                size={ButtonSize.MINI}
                icon="edit"
                text="Edit"
                className={cx(props.editDisabledMessage == null && classes.editButtonActive)}
                disabled={props.editDisabledMessage != null}
                onClick={() => {
                  const eventGroupId = makeAnalyticsEventId();
                  setPcmEditorEventGroupId(eventGroupId);
                  trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
                    event_group_id: eventGroupId,
                    action_origin: props.actionOrigin,
                    action_method: ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION,
                    task_schema_api_id: props.rootProcessTaskSchemaId,
                    modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
                    process_id: props.flowchartProcessId,
                  });
                  setPreEditDraftSnapshot(draftState);
                  openPcmEditor();
                }}
                dataTestAttrs={{
                  component: 'TaskGroupFlowchartExecutionViewer',
                  element: 'openProcessUpdateFlowchartModal',
                }}
              />
            </TooltipWrapper>
          </Box>
        )}
      </Box>
      <Box className={classes.pane}>
        {props.isFlowchartMappingInProgress && (
          <Box className={classes.mappingInProgressIndicator}>
            <MappingInProgressIndicator
              text="Mapping in progress..."
              dataTestAttrs={{
                component: 'TaskGroupFlowchartExecutionViewer',
                element: 'mappingInProgressIndicator',
              }}
            />
          </Box>
        )}
        {!pcmEditorOpened && props.displayOnlyFlowchart != null && (
          <ReadOnlyFlowchart
            flowchart={props.displayOnlyFlowchart}
            onNodeNavigateClick={onNodeNavigateClick}
            selectedNodeConfigId={selectedNodeConfigId}
          />
        )}
        {!pcmEditorOpened && props.displayOnlyFlowchart == null && (
          <ExecutionFlowchartPane
            editorMode={FlowchartEditorMode.EXECUTION_VIEWER}
            processId={props.flowchartProcessId}
            draftState={draftState}
            setDraftState={setDraftState}
            readinessSnapshot={readinessSnapshot}
            onNodeNavigateClick={onNodeNavigateClick}
            selectedNodeConfigId={selectedNodeConfigId}
          />
        )}
      </Box>
      <Modal
        opened={pcmEditorOpened}
        onClose={handlePcmEditorExit}
        title="Update flowchart"
        size="98%"
        centered={true}
        closeOnEscape={false}
        padding={0}
        withinPortal={!isNodeTesting}
        styles={{
          content: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '100%',
            height: '90vh',
            maxHeight: '90vh',
          },
          inner: {padding: '0 !important'},
          body: {
            padding: '0 !important',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          },
        }}
      >
        {pcmEditorOpened ? (
          <ImprovedProcessUpdateFlowchartModal
            flowchartProcessId={props.flowchartProcessId}
            sourceType={props.sourceType}
            onFlowchartModified={props.onFlowchartModified}
            eventGroupId={pcmEditorEventGroupId}
            actionOrigin={props.actionOrigin}
            draftState={draftState}
            setDraftState={setDraftState}
            readinessSnapshot={readinessSnapshot}
            initialDraftState={preEditDraftSnapshot}
            onCancel={revertDraftAndClose}
            onSubmit={handlePcmEditorSubmit}
          />
        ) : null}
      </Modal>
    </Box>
  );
}
